import type { ReactNode } from 'react';

import { Button } from '../Button';
import { Modal, type ModalSize } from '../Modal';
import { PreviewTable } from '../PreviewTable';
import type { DataColumn } from './DataTable';

/**
 * One row of a panel table, read as a record.
 *
 * A table row answers "how does this compare to the others"; it is a poor way to read one
 * thing, because the columns a reader needs are spread across a scroll and the narrow ones are
 * truncated. This is the same row down the page instead — the Table Display View, a key
 * against a value, nothing clipped.
 *
 * **The rows come from the table's own `columns`, and that is the whole point.** The preview
 * cannot drift from the table, cannot miss a column somebody added, and cannot label one
 * differently: there is one declaration and two renderings of it.
 *
 * **`details` is the one thing a column cannot supply, and it APPENDS rather than replaces.**
 * A column can say `12` about a list of twelve declared assets and nothing more — so a record
 * whose content is a child collection had a preview showing its own totals and not one of the
 * lines they were totalled from. The column-derived rows above it are untouched: this is an
 * addition to the preview, never a second way of writing one.
 *
 * Reach for it ONLY for a collection. A scalar the reader is missing is a missing COLUMN, and
 * the fix for that is to declare it — putting it here instead would put a fact in the preview
 * that the table cannot show, which is the drift the column derivation exists to prevent.
 *
 * **`size` matches the record's own EDITOR, and the panel says which.** The preview and the
 * form over one record are the same record at the same width — a reader opening a filing and
 * then correcting it should not watch the dialog change shape under them, and a preview held at
 * one size while its editor grew is a table squeezed into a third of the room it has in the
 * form. It defaults to `lg`, which is what every caller was implicitly taking.
 *
 * **It writes nothing, so it carries no confirm tick** — that rule gates the primary action of
 * a transaction, and this modal has no primary action at all. Close is the only control.
 */
export function RowPreviewModal<T>({
  open,
  row,
  columns,
  title,
  details,
  size = 'lg',
  onClose,
  id,
}: {
  open: boolean;
  /** Null closes it: a preview of no row is not a thing to render. */
  row: T | null;
  columns: DataColumn<T>[];
  title: string;
  /** The row's child collections, drawn under the key/value table. See the note above. */
  details?: (row: T) => ReactNode;
  /** Match the record's own editor. See the note above. */
  size?: ModalSize;
  onClose: () => void;
  id: string;
}) {
  if (!open || row === null) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size={size}
      id={`${id}-modal`}
      footer={
        <Button id={`${id}-close`} variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex min-w-0 flex-col gap-5">
        <PreviewTable
          variant="table-display"
          /*
            A column with NO HEADER is not a fact — it is a gutter carrying a control, which
            every table here ends with as its kebab. Mapped into a key/value table it would put a
            row-actions menu under a blank label, offering Edit and Delete from inside a preview
            that exists to be read.

            Filtered on the empty header rather than on the key: the header is what makes a
            column a fact, and the next control column will not be named `actions`.
          */
          rows={columns
            .filter((column) => column.header.trim() !== '')
            .map((column) => ({
              label: column.header,

              /*
                The column's own `render`, so a date is formatted by the same call that formats
                it in the table and a status is the same pill. A second formatting path here is
                how the two surfaces come to disagree about one value.
              */
              value: column.render(row),

              /*
                No `mono`. The column's `render` already returns a cell that decided its own
                face — `<TextCell mono>`, `<DateCell>` — and setting it here would put the
                label's sibling in a face the cell inside it had already refused.
              */
            }))}
        />

        {details ? details(row) : null}
      </div>
    </Modal>
  );
}
